<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="500px">
    <v-card>
      <v-card-title>
        <span class="text-h5">{{ isEditing ? 'Ansprechpartnerin bearbeiten' : 'Neue Ansprechpartnerin' }}</span>
      </v-card-title>

      <v-card-text>
        <v-form ref="contactPersonForm">

          <v-text-field
            v-model="formData.name"
            label="Name*"
            required
            @input="debouncedCheckDuplicates"
            :error-messages="duplicateWarning"
          ></v-text-field>

          <v-text-field
            v-model="formData.position"
            label="Position"
          ></v-text-field>

          <v-text-field
            v-model="formData.email"
            label="Email"
            type="email"
          ></v-text-field>

          <v-text-field
            v-model="formData.phone"
            label="Telefon"
            @input="formatPhoneInput"
          ></v-text-field>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close">Abbrechen</v-btn>
        <v-btn v-if="isEditing" color="error" @click="deleteContact">Löschen</v-btn>
        <v-btn 
          color="primary" 
          @click="save"
          :disabled="!formData.name || potentialDuplicates.length > 0"
        >
          Speichern
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>

let fuzz
try {
  fuzz = require('fuzzball')
} catch (e) {
  try {
    const fuzzballModule = require('fuzzball')
    fuzz = fuzzballModule.default || fuzzballModule
  } catch (e2) {
    console.error('Could not load fuzzball:', e2)
  }
}

export default {
  name: 'ContactPersonDialog',

  props: {
    value: Boolean,
    contact: Object,
    customer: Object,
    existingContacts: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      formData: {
        name: '',
        position: '',
        email: '',
        phone: ''
      },
      potentialDuplicates: [],
      duplicateThreshold: 75, 
      debounceTimer: null
    }
  },

  computed: {
    isEditing() {
      return !!this.contact
    },

    duplicateWarning() {
      if (this.potentialDuplicates.length > 0) {
        return [`${this.potentialDuplicates.length} ähnliche Kontakt(e) gefunden!`]
      }
      return []
    }
  },

  watch: {
    value(newVal) {
      if (newVal) {
        this.loadContactData()
        this.$nextTick(() => {
          this.checkForDuplicates()
        })
      }
    },
    
    contact(newVal) {
      if (newVal && this.value) {
        this.loadContactData()
      }
    }
  },

  methods: {
    loadContactData() {
      if (this.contact) {
        this.formData = { ...this.contact }
      } else {
        this.formData = {
          name: '',
          position: '',
          email: '',
          phone: ''
        }
      }
      this.potentialDuplicates = []
    },

    formatPhoneInput() {
      if (this.formData.phone) {
        this.formData.phone = this.formData.phone.replace(/[^\d+]/g, '')
      }
    },

    debouncedCheckDuplicates() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }
      
      this.debounceTimer = setTimeout(() => {
        this.checkForDuplicates()
      }, 300)
    },

    checkForDuplicates() {
      console.log('🔍 Checking for duplicates...')
      console.log('Form data:', JSON.stringify(this.formData, null, 2))
      console.log('Existing contacts count:', this.existingContacts.length)
      
      // Clear previous duplicates
      this.potentialDuplicates = []

      // Don't check if name is too short
      if (!this.formData.name || this.formData.name.trim().length < 3) {
        console.log('⚠️ Name too short, skipping duplicate check')
        return
      }

      // Filter out current contact if editing
      const contactsToCheck = this.existingContacts.filter(c => {
        if (this.isEditing && this.contact && this.contact.id) {
          return c.id !== this.contact.id
        }
        return true
      })

      console.log('📋 Contacts to check:', contactsToCheck.length)

      // Check each existing contact - ONLY NAME
      contactsToCheck.forEach(existingContact => {
        // Check name similarity only
        if (existingContact.name) {
          const nameScore = this.calculateSimilarity(
            this.formData.name.toLowerCase().trim(),
            existingContact.name.toLowerCase().trim()
          )
          
          console.log(`📊 Name comparison: "${this.formData.name}" vs "${existingContact.name}" = ${nameScore}%`)
          
          if (nameScore >= this.duplicateThreshold) {
            console.log(`✓ MATCH FOUND! Score: ${nameScore}%`)
            console.log('🚨 DUPLICATE DETECTED:', existingContact.name, 'Score:', nameScore)
            this.potentialDuplicates.push({
              ...existingContact,
              score: Math.round(nameScore)
            })
          }
        }
      })

      // Sort by score (highest first)
      this.potentialDuplicates.sort((a, b) => b.score - a.score)
      
      console.log('✅ Total duplicates found:', this.potentialDuplicates.length)
      if (this.potentialDuplicates.length > 0) {
        console.log('🔴 DUPLICATES:', this.potentialDuplicates)
      }
    },

    calculateSimilarity(str1, str2) {
      // If fuzzball is available, use it
      if (fuzz && typeof fuzz.ratio === 'function') {
        return fuzz.ratio(str1, str2)
      }
      
      // Fallback to built-in method
      console.warn('⚠️ Fuzzball not available, using fallback method')
      
      // Exact match
      if (str1 === str2) return 100

      // One string contains the other
      if (str1.includes(str2) || str2.includes(str1)) {
        return 90
      }

      // Levenshtein distance
      const longer = str1.length > str2.length ? str1 : str2
      const shorter = str1.length > str2.length ? str2 : str1
      
      if (longer.length === 0) return 100
      
      const editDistance = this.getEditDistance(longer, shorter)
      return Math.round(((longer.length - editDistance) / longer.length) * 100)
    },

    getEditDistance(str1, str2) {
      const matrix = []

      for (let i = 0; i <= str2.length; i++) {
        matrix[i] = [i]
      }

      for (let j = 0; j <= str1.length; j++) {
        matrix[0][j] = j
      }

      for (let i = 1; i <= str2.length; i++) {
        for (let j = 1; j <= str1.length; j++) {
          if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
            matrix[i][j] = matrix[i - 1][j - 1]
          } else {
            matrix[i][j] = Math.min(
              matrix[i - 1][j - 1] + 1,
              matrix[i][j - 1] + 1,
              matrix[i - 1][j] + 1
            )
          }
        }
      }

      return matrix[str2.length][str1.length]
    },

    normalizePhone(phone) {
      if (!phone) return ''
      return phone.replace(/[^\d+]/g, '')
    },

    close() {
      this.potentialDuplicates = []
      this.$emit('input', false)
    },

    save() {
      if (!this.formData.name || this.formData.name.trim().length === 0) {
        alert('Bitte geben Sie einen Namen ein')
        return
      }

      // Prevent save if duplicates found
      if (this.potentialDuplicates.length > 0) {
        alert('⚠️ Speichern nicht möglich: Es wurden ähnliche Kontakte gefunden. Bitte ändern Sie die Daten oder löschen Sie die Duplikate.')
        return
      }

      const contactData = {
        ...this.formData,
        customer_id: this.customer.id
      }

      if (this.isEditing) {
        contactData.id = this.contact.id
        contactData.updated_at = new Date().toISOString()
      } else {
        contactData.created_at = new Date().toISOString()
        contactData.updated_at = new Date().toISOString()
      }

      this.$emit('save', contactData)
      this.potentialDuplicates = []
    },

    deleteContact() {
      if (!confirm('Möchten Sie diese Ansprechpartnerin wirklich löschen?')) {
        return
      }
      this.$emit('delete')
    }
  },

  beforeDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  }
}
</script>

<style scoped>
.v-alert--prominent {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}
</style>